import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { firstValueFrom } from 'rxjs';
import { CustomerFactory } from '@contracts/factories/customer.factory';
import { InvoiceFactory } from '@contracts/factories/invoice.factory';
import { CustomerInterface } from '@contracts/interfaces/customer.interface';
import { InventoryCategory } from '@contracts/interfaces/inventory-category';
import { InventoryItem, InventoryItemFeature } from '@contracts/interfaces/inventory-item.interface';
import { InvoiceInterface, InvoiceStatus } from '@contracts/interfaces/invoice.interface';
import { LineItem } from '@contracts/interfaces/lineitem.interface';
import { Media } from '@contracts/interfaces/media.interfaces';
import { PartsOrder, PartsOrderStatus } from '@contracts/interfaces/parts-order.interface';
import { CalendarEntry, CalendarEntryStatus } from '@contracts/interfaces/calendar.interface';
import { Task, TaskStatus } from '@contracts/interfaces/task.interface';
import { DateRange, Transaction, TransactionType } from '@contracts/interfaces/transaction.interfaces';
import { InventoryItemFactory } from 'src/app/factories/inventory-item.factory';
import { CustomerDialogComponent } from 'src/app/dialogs/customer/customer.dialog';
import { RequestReviewDialog } from 'src/app/dialogs/request-review/request-review.dialog';
import { TransactionDialogComponent } from 'src/app/dialogs/transaction-dialog/transaction.dialog';
import { ItemSearchDialogComponent } from 'src/app/dialogs/item-search/item-search.dialog';
import { DateFilterDialogComponent } from 'src/app/dialogs/date-filter/date-filter.dialog';
import { InvoiceDeleteDialogComponent } from 'src/app/dialogs/invoice-delete/invoice-delete.dialog';
import { PendingEvidenceViewerDialogComponent } from 'src/app/dialogs/pending-evidence-viewer/pending-evidence-viewer.dialog';
import { CustomerImportDialogComponent } from 'src/app/dialogs/customer-import/customer-import.dialog';
import { JsonViewerDialogComponent } from 'src/app/dialogs/json-viewer/json-viewer.dialog';
import { RecordPaymentDialogComponent } from 'src/app/dialogs/record-payment/record-payment.dialog';
import { EmailInvoiceDialogComponent } from 'src/app/dialogs/email-invoice/email-invoice.dialog';
import { EmailReceiptDialogComponent } from 'src/app/dialogs/email-receipt/email-receipt.dialog';
import { SendSMSDialog } from 'src/app/dialogs/send-sms/send-sms.dialog';
import { InvoicePromptDialogComponent } from 'src/app/dialogs/invoice-prompt/invoice-prompt.dialog';
import { ImageDialogComponent } from 'src/app/dialogs/image-dialog/image.dialog';
import { InventoryWebsiteDialogComponent } from 'src/app/dialogs/inventory-website/inventory-website.dialog';
import { PdfGenerateComponent } from 'src/app/dialogs/pdf-generate/pdf-generate.component';
import { ImageDownloadDialogComponent } from 'src/app/dialogs/image-download-dialog/image-download-dialog.component';
import { SalesChannelDialog } from 'src/app/dialogs/sales-channel-dialog/sales-channel.dialog';
import { EbayRevenueDialogComponent } from 'src/app/dialogs/ebay-revenue-dialog/ebay-revenue.dialog';
import { SquareRevenueDialogComponent } from 'src/app/dialogs/square-revenue-dialog/square-revenue.dialog';
import { InventoryItemDialogComponent } from 'src/app/dialogs/inventory-item-dialog/inventory-item.dialog';
import { TaskDialogComponent } from 'src/app/dialogs/task-dialog/task.dialog';
import { InventoryItemFeatureDialogComponent } from 'src/app/dialogs/inventory-item-feature/inventory-item-feature.dialog';
import { JobProgressDialogComponent } from 'src/app/dialogs/job-progress/job-progress.component';
import { SelectOptionDialog } from 'src/app/dialogs/select-option-dialog/select-option.dialog';
import { SnoozeDialogComponent } from 'src/app/dialogs/snooze-dialog/snooze.dialog';
import { InvoiceLineItemFindDialogComponent } from 'src/app/dialogs/invoice-line-item-find/invoice-line-item-find.dialog';
import { InvoiceDialogComponent } from 'src/app/dialogs/invoice-dialog/invoice.dialog';
import { InventoryCatRenameDialogComponent } from 'src/app/dialogs/inventory-cat-rename-dialog/inventory-cat-rename.dialog';
import { ExpensesTaxFiguresDialogComponent } from 'src/app/dialogs/expenses-tax-figures/expenses-tax-figures.dialog';
import { InventoryItemTemplateDialogComponent } from 'src/app/dialogs/inventory-item-template-dialog/inventory-item-template.dialog';
import { InventoryCategoryNewDialogComponent } from 'src/app/dialogs/inventory-category-new-dialog/inventory-category-new.dialog';
import { RunSheetDialogComponent } from 'src/app/dialogs/run-sheet-dialog/run-sheet.dialog';
import { PDFDialogComponent } from 'src/app/dialogs/pdf-dialog/pdf.dialog';
import { inventoryCategoriesDialogComponent } from 'src/app/dialogs/inventory-categories-dialog/inventory-categories.dialog';
import { CashDialogComponent } from 'src/app/dialogs/cash-dialog/cash.dialog';
import { KiosModeDialog } from 'src/app/dialogs/kios-mode/kios-mode.dialog';
import { PayPayrollDialogComponent } from 'src/app/dialogs/payroll-pay/payroll-pay.dialog';
import { RemoteClientDialogComponent } from 'src/app/dialogs/remote-client-dialog/remote-client-dialog.component';
import { TransactionRangeDialogComponent } from 'src/app/dialogs/transaction-range/transaction-range.dialog';
import { TransactionPresetDialogComponent } from 'src/app/dialogs/transaction-preset-dialog/transaction-preset.dialog';
import { PartsOrderDialog } from 'src/app/dialogs/parts-order-dialog/parts-order.dialog';
import { CalendarEntryDialogComponent } from 'src/app/dialogs/calendar-entry-dialog/calendar-entry-dialog.component';
import { CalendarPickerDialogComponent } from 'src/app/dialogs/calendar-picker-dialog/calendar-picker-dialog.component';
import { PaymentDetailDialogComponent } from 'src/app/dialogs/payment-detail/payment-detail.dialog';
import { ReceiptUploadDialogComponent } from 'src/app/dialogs/receipt-upload-dialog/receipt-upload.dialog';
import { InventoryService } from 'src/app/services/inventory.service';
import { InventoryCategoryService } from 'src/app/services/inventory-category.service';
import { InvoicesService } from 'src/app/services/invoices.service';
import { TransactionsService } from 'src/app/services/transactions.service';
import { TaskService } from 'src/app/services/task.service';
import { PartsOrderService } from 'src/app/services/parts-order.service';

interface DialogLabEntry {
  title: string;
  group: string;
  mode: 'Stub' | 'Live';
  description: string;
  action: () => Promise<void>;
}

@Component({
  selector: 'app-dialog-lab',
  templateUrl: './dialog-lab.component.html',
  styleUrls: ['./dialog-lab.component.scss']
})
export class DialogLabComponent implements OnInit {
  public search = '';
  public opening = '';
  public status = 'Ready';
  public entries: DialogLabEntry[] = [];

  private liveInvoice: InvoiceInterface | null | undefined;
  private liveTransaction: Transaction | null | undefined;
  private liveTask: Task | null | undefined;
  private liveInventoryItem: InventoryItem | null | undefined;
  private liveCategory: InventoryCategory | null | undefined;
  private liveMedia: Media | null | undefined;

  constructor(
    private dialog: MatDialog,
    private inventoryService: InventoryService,
    private inventoryCategoryService: InventoryCategoryService,
    private invoicesService: InvoicesService,
    private transactionsService: TransactionsService,
    private taskService: TaskService,
    private partsOrderService: PartsOrderService
  ) {}

  ngOnInit(): void {
    this.entries = this.buildEntries();
  }

  get filteredEntries(): DialogLabEntry[] {
    const term = this.search.trim().toLowerCase();
    if (!term) {
      return this.entries;
    }

    return this.entries.filter(entry =>
      [entry.title, entry.group, entry.mode, entry.description].join(' ').toLowerCase().includes(term)
    );
  }

  get liveEntryCount(): number {
    return this.entries.filter(entry => entry.mode === 'Live').length;
  }

  get stubEntryCount(): number {
    return this.entries.filter(entry => entry.mode === 'Stub').length;
  }

  async open(entry: DialogLabEntry): Promise<void> {
    this.opening = entry.title;
    this.status = `Opening ${entry.title}...`;

    try {
      await entry.action();
      this.status = `${entry.title} opened`;
    } catch (error: any) {
      this.status = error?.message || `Could not open ${entry.title}`;
    } finally {
      this.opening = '';
    }
  }

  private buildEntries(): DialogLabEntry[] {
    return [
      { title: 'Customer', group: 'Customers', mode: 'Stub', description: 'Customer create or edit window.', action: async () => this.openDialog(CustomerDialogComponent, { width: '900px', data: { customer: null, setName: 'Window Test Customer' } }) },
      { title: 'Customer Import', group: 'Customers', mode: 'Stub', description: 'Contact importer flow.', action: async () => this.openDialog(CustomerImportDialogComponent, { width: '800px', data: { category: 'test' } }) },
      { title: 'Request Review', group: 'Customers', mode: 'Stub', description: 'Review request SMS dialog.', action: async () => this.openDialog(RequestReviewDialog, { width: '680px', data: { customer: this.sampleCustomer() } }) },
      { title: 'Send SMS', group: 'Customers', mode: 'Stub', description: 'Generic outbound SMS composer.', action: async () => this.openDialog(SendSMSDialog, { width: '680px', data: { customer: this.sampleCustomer(), template: 'Hi {{name}}, your device is ready for collection.' } }) },
      { title: 'Item Search', group: 'General', mode: 'Stub', description: 'Simple item picker shell.', action: async () => this.openDialog(ItemSearchDialogComponent, { width: '520px', data: {} }) },
      { title: 'Date Filter', group: 'General', mode: 'Stub', description: 'Date range selection dialog.', action: async () => this.openDialog(DateFilterDialogComponent, { width: '520px', data: {} }) },
      { title: 'JSON Viewer', group: 'General', mode: 'Stub', description: 'Inspector for raw payloads.', action: async () => this.openDialog(JsonViewerDialogComponent, { width: '820px', data: { data: { sample: true, when: new Date().toISOString() }, title: 'Dialog lab payload' } }) },
      { title: 'Select Option', group: 'General', mode: 'Stub', description: 'Option list popup.', action: async () => this.openDialog(SelectOptionDialog, { width: '520px', data: { feature: 'Status', current: 'In progress', options: ['Queued', 'In progress', 'Completed'] } }) },
      { title: 'Job Progress', group: 'General', mode: 'Stub', description: 'Background job progress stub.', action: async () => this.openDialog(JobProgressDialogComponent, { width: '520px', data: { jobID: 'dialog-lab-job' } }) },
      { title: 'Invoice Prompt', group: 'Invoices', mode: 'Stub', description: 'Prompt to invoice draft input.', action: async () => this.openDialog(InvoicePromptDialogComponent, { width: '720px', data: { prompt: 'Create an invoice for onsite support, labour and a replacement SSD.' } }) },
      { title: 'Invoice Dialog', group: 'Invoices', mode: 'Stub', description: 'Create-invoice editor in draft mode.', action: async () => this.openDialog(InvoiceDialogComponent, { width: '1250px', height: '90vh', data: { type: 'standard' } }) },
      { title: 'Invoice Delete', group: 'Invoices', mode: 'Stub', description: 'Delete confirmation window.', action: async () => this.openDialog(InvoiceDeleteDialogComponent, { width: '520px', data: { invoice: { number: 2401 } } }) },
      { title: 'Record Payment', group: 'Invoices', mode: 'Stub', description: 'Payment entry dialog.', action: async () => this.openDialog(RecordPaymentDialogComponent, { width: '680px', data: { invoice: this.sampleInvoice(), refund: false } }) },
      { title: 'Email Invoice', group: 'Invoices', mode: 'Stub', description: 'Invoice email composer.', action: async () => this.openDialog(EmailInvoiceDialogComponent, { width: '920px', data: { invoice: this.sampleInvoice(), pdfHash: 'dialog-lab-pdf' } }) },
      { title: 'Email Receipt', group: 'Invoices', mode: 'Stub', description: 'Receipt email composer.', action: async () => this.openDialog(EmailReceiptDialogComponent, { width: '920px', data: { invoice: this.samplePaidInvoice() } }) },
      { title: 'Invoice Line Item Find', group: 'Invoices', mode: 'Stub', description: 'Select reusable line items.', action: async () => this.openDialog(InvoiceLineItemFindDialogComponent, { width: '720px', data: { prefillLineItems: this.sampleLineItems() } }) },
      { title: 'Payment Detail', group: 'Invoices', mode: 'Stub', description: 'Payment linking and suggestions.', action: async () => this.openDialog(PaymentDetailDialogComponent, { width: '860px', data: this.samplePaymentDetail() }) },
      { title: 'PDF Preview', group: 'Invoices', mode: 'Stub', description: 'Generated PDF preview window.', action: async () => this.openDialog(PDFDialogComponent, { width: '1120px', data: { PDFTemplate: 'test', data: { title: 'Dialog lab PDF' } } }) },
      { title: 'PDF Generate', group: 'Invoices', mode: 'Stub', description: 'Loading state for PDF generation.', action: async () => this.openDialog(PdfGenerateComponent, { width: '420px', data: { PDFTemplate: 'test', data: { title: 'Dialog lab PDF' } } }) },
      { title: 'Transaction Dialog', group: 'Transactions', mode: 'Live', description: 'Full transaction workbench using a live record.', action: async () => this.openTransactionDialog() },
      { title: 'Transaction Range', group: 'Transactions', mode: 'Stub', description: 'Date-range chooser for reports and receipts.', action: async () => this.openDialog(TransactionRangeDialogComponent, { width: '720px', data: { dateRange: this.sampleDateRange(), account: 'revenue' } }) },
      { title: 'Expenses Tax Figures', group: 'Transactions', mode: 'Stub', description: 'Tax summary window for expenses.', action: async () => this.openDialog(ExpensesTaxFiguresDialogComponent, { width: '960px', data: { dateRange: this.sampleDateRange() } }) },
      { title: 'Transaction Presets', group: 'Transactions', mode: 'Live', description: 'Preset manager backed by the API.', action: async () => this.openDialog(TransactionPresetDialogComponent, { width: '1100px', data: {} }) },
      { title: 'eBay Revenue', group: 'Transactions', mode: 'Live', description: 'Marketplace payout reconciliation.', action: async () => this.openRevenueDialog(EbayRevenueDialogComponent) },
      { title: 'Square Revenue', group: 'Transactions', mode: 'Live', description: 'Square payout reconciliation.', action: async () => this.openRevenueDialog(SquareRevenueDialogComponent) },
      { title: 'Cash Revenue', group: 'Transactions', mode: 'Live', description: 'Cash deposit reconciliation.', action: async () => this.openRevenueDialog(CashDialogComponent, { width: '440px' }) },
      { title: 'Pending Evidence Viewer', group: 'Transactions', mode: 'Stub', description: 'Pending proof review window.', action: async () => this.openDialog(PendingEvidenceViewerDialogComponent, { width: '1100px', data: { index: 0, pendingEvidenceList: [{ file: 'dialog-lab-missing-proof.png', business: 'Dialog Lab', amount: 12500, note: 'Sample pending evidence' }] } }) },
      { title: 'Receipt Upload', group: 'Transactions', mode: 'Stub', description: 'Receipt upload tray.', action: async () => this.openDialog(ReceiptUploadDialogComponent, { width: '760px', data: {} }) },
      { title: 'Task Dialog', group: 'Tasks', mode: 'Live', description: 'Primary task workspace.', action: async () => this.openTaskDialog() },
      { title: 'Snooze Task', group: 'Tasks', mode: 'Stub', description: 'Task snooze presets.', action: async () => this.openDialog(SnoozeDialogComponent, { width: '900px', data: { task: this.sampleTask() } }) },
      { title: 'Run Sheet', group: 'Tasks', mode: 'Live', description: 'Active-task run sheet.', action: async () => this.openDialog(RunSheetDialogComponent, { width: '1200px', data: {} }) },
      { title: 'Parts Order', group: 'Tasks', mode: 'Stub', description: 'Parts ordering editor.', action: async () => this.openDialog(PartsOrderDialog, { width: '720px', data: { order: this.samplePartsOrder() } }) },
      { title: 'Calendar Entry', group: 'Tasks', mode: 'Stub', description: 'Calendar entry editor.', action: async () => this.openDialog(CalendarEntryDialogComponent, { width: '620px', data: { entry: this.sampleCalendarEntry(), isNew: false } }) },
      { title: 'Calendar Picker', group: 'Tasks', mode: 'Stub', description: 'Date picker popup.', action: async () => this.openDialog(CalendarPickerDialogComponent, { width: '420px', data: { selected: new Date() } }) },
      { title: 'Remote Client', group: 'Tasks', mode: 'Stub', description: 'Remote support client editor.', action: async () => this.openDialog(RemoteClientDialogComponent, { width: '760px', data: { mode: 'create', client: null } }) },
      { title: 'Pay Payroll', group: 'Tasks', mode: 'Stub', description: 'Payroll bank transfer window.', action: async () => this.openDialog(PayPayrollDialogComponent, { width: '860px', data: {} }) },
      { title: 'Kiosk Mode', group: 'Tasks', mode: 'Live', description: 'Full-screen kiosk shell.', action: async () => this.openDialog(KiosModeDialog, { width: '100vw', maxWidth: '100vw', height: '100vh', panelClass: ['kios'], data: {} }, false) },
      { title: 'Inventory Item', group: 'Inventory', mode: 'Live', description: 'Full inventory item workspace.', action: async () => this.openInventoryItemDialog() },
      { title: 'Inventory Feature', group: 'Inventory', mode: 'Stub', description: 'Feature editor.', action: async () => this.openDialog(InventoryItemFeatureDialogComponent, { width: '720px', data: { itemFeature: this.sampleFeature(), itemFeatureValue: 'Intel Core i7 / 16GB RAM / 512GB SSD' } }) },
      { title: 'Inventory Website', group: 'Inventory', mode: 'Stub', description: 'Website listing placeholder dialog.', action: async () => this.openDialog(InventoryWebsiteDialogComponent, { width: '860px', data: {} }) },
      { title: 'Sales Channel', group: 'Inventory', mode: 'Live', description: 'Sales channel editor.', action: async () => this.openLiveInventoryDialog(SalesChannelDialog, { width: '900px' }, item => ({ item, salesChannel: { title: item.title } })) },
      { title: 'Image Editor', group: 'Inventory', mode: 'Live', description: 'Media editor using a live asset.', action: async () => this.openMediaDialog(ImageDialogComponent, { width: '95vw', maxWidth: '95vw' }, media => ({ media, view: true, index: 0, list: [media] })) },
      { title: 'Image Download', group: 'Inventory', mode: 'Live', description: 'Image download/export dialog.', action: async () => this.openMediaDialog(ImageDownloadDialogComponent, { width: '760px' }, media => ({ media, saveAsFileName: media.filename, direct: false })) },
      { title: 'Inventory Category New', group: 'Inventory', mode: 'Stub', description: 'New category creator.', action: async () => this.openDialog(InventoryCategoryNewDialogComponent, { width: '620px', data: {} }) },
      { title: 'Inventory Category Rename', group: 'Inventory', mode: 'Live', description: 'Rename existing category.', action: async () => this.openCategoryDialog(InventoryCatRenameDialogComponent, { width: '620px' }, category => ({ category })) },
      { title: 'Inventory Categories', group: 'Inventory', mode: 'Live', description: 'Category management board.', action: async () => this.openDialog(inventoryCategoriesDialogComponent, { width: '1200px', data: {} }) },
      { title: 'Inventory Template', group: 'Inventory', mode: 'Live', description: 'Category item-template editor.', action: async () => this.openCategoryDialog(InventoryItemTemplateDialogComponent, { width: '1200px', maxWidth: '1200px' }, category => ({ categoryID: category.id })) }
    ];
  }

  private async openTransactionDialog(): Promise<void> {
    const transaction = await this.getLiveTransaction();
    if (!transaction) {
      throw new Error('No transaction records were available.');
    }

    this.openDialog(TransactionDialogComponent, {
      width: '90vw',
      maxWidth: '90vw',
      height: '100vh',
      data: {
        type: this.transactionsService.getTransactionType(transaction) || TransactionType.Expense,
        transactionHash: transaction.source.sha1,
        index: 0,
        dataSource: new MatTableDataSource<Transaction>([transaction])
      }
    });
  }

  private async openRevenueDialog(component: any, config: MatDialogConfig = {}): Promise<void> {
    const transaction = await this.getLiveTransaction();
    if (!transaction) {
      throw new Error('No transaction records were available.');
    }

    this.openDialog(component, {
      width: '900px',
      ...config,
      data: { transaction }
    });
  }

  private async openTaskDialog(): Promise<void> {
    const task = await this.getLiveTask();
    const dialogTask = task || this.sampleTask();

    this.openDialog(TaskDialogComponent, {
      width: '1200px',
      height: '100vh',
      data: {
        task: dialogTask,
        list: dialogTask.id ? [dialogTask.id] : [],
        currentIndex: 0
      }
    });
  }

  private async openInventoryItemDialog(): Promise<void> {
    const item = await this.getLiveInventoryItem();
    if (!item) {
      throw new Error('No inventory items were available.');
    }

    this.openDialog(InventoryItemDialogComponent, {
      width: '95vw',
      maxWidth: '1130px',
      height: '95vh',
      data: {
        item,
        list: [item.number],
        currentIndex: 0
      }
    });
  }

  private async openLiveInventoryDialog(component: any, config: MatDialogConfig, dataFactory: (item: InventoryItem) => any): Promise<void> {
    const item = await this.getLiveInventoryItem();
    if (!item) {
      throw new Error('No inventory items were available.');
    }

    this.openDialog(component, {
      ...config,
      data: dataFactory(item)
    });
  }

  private async openCategoryDialog(component: any, config: MatDialogConfig, dataFactory: (category: InventoryCategory) => any): Promise<void> {
    const category = await this.getLiveCategory();
    if (!category) {
      throw new Error('No inventory categories were available.');
    }

    this.openDialog(component, {
      ...config,
      data: dataFactory(category)
    });
  }

  private async openMediaDialog(component: any, config: MatDialogConfig, dataFactory: (media: Media) => any): Promise<void> {
    const media = await this.getLiveMedia();
    if (!media) {
      throw new Error('No inventory media records were available.');
    }

    this.openDialog(component, {
      ...config,
      data: dataFactory(media)
    });
  }

  private openDialog(component: any, config: MatDialogConfig = {}, useWindowShell: boolean = true): void {
    const merged = { ...config };
    if (useWindowShell) {
      const classes = Array.isArray(merged.panelClass)
        ? merged.panelClass
        : merged.panelClass
          ? [merged.panelClass]
          : [];
      merged.panelClass = ['app-dialog-window', ...classes];
      merged.autoFocus = merged.autoFocus ?? false;
    }

    this.dialog.open(component, merged);
  }

  private async getLiveInvoice(): Promise<InvoiceInterface | null> {
    if (this.liveInvoice !== undefined) {
      return this.liveInvoice;
    }

    try {
      const invoices = await firstValueFrom(this.invoicesService.find({}));
      this.liveInvoice = invoices?.[0] || null;
    } catch (_) {
      this.liveInvoice = null;
    }

    return this.liveInvoice;
  }

  private async getLiveTransaction(): Promise<Transaction | null> {
    if (this.liveTransaction !== undefined) {
      return this.liveTransaction;
    }

    try {
      const transactions = await firstValueFrom(this.transactionsService.find({}, 0, 1, { date: -1 }));
      this.liveTransaction = transactions?.[0] || null;
    } catch (_) {
      this.liveTransaction = null;
    }

    return this.liveTransaction;
  }

  private async getLiveTask(): Promise<Task | null> {
    if (this.liveTask !== undefined) {
      return this.liveTask;
    }

    try {
      const tasks: any = await firstValueFrom(this.taskService.find({}));
      this.liveTask = tasks?.[0] || null;
    } catch (_) {
      this.liveTask = null;
    }

    return this.liveTask;
  }

  private async getLiveInventoryItem(): Promise<InventoryItem | null> {
    if (this.liveInventoryItem !== undefined) {
      return this.liveInventoryItem;
    }

    try {
      const items = await firstValueFrom(this.inventoryService.find({}, 'number', 'desc', 1));
      this.liveInventoryItem = items?.[0] || null;
    } catch (_) {
      this.liveInventoryItem = null;
    }

    return this.liveInventoryItem;
  }

  private async getLiveCategory(): Promise<InventoryCategory | null> {
    if (this.liveCategory !== undefined) {
      return this.liveCategory;
    }

    try {
      const categories = await firstValueFrom(this.inventoryCategoryService.find({}));
      this.liveCategory = categories?.[0] || null;
    } catch (_) {
      this.liveCategory = null;
    }

    return this.liveCategory;
  }

  private async getLiveMedia(): Promise<Media | null> {
    if (this.liveMedia !== undefined) {
      return this.liveMedia;
    }

    const item = await this.getLiveInventoryItem();
    this.liveMedia = item?.media?.[0] || item?.ebayImages?.[0] || null;
    return this.liveMedia;
  }

  private sampleCustomer(): CustomerInterface {
    const customer = CustomerFactory.validate(CustomerFactory.newCustomer());
    customer.name = 'Dialog Lab Customer';
    customer.email = 'lab@example.com';
    customer.phone = '0412345678';
    customer.address = '123 Test Street';
    customer.notes = 'Used for dialog launcher previews.';
    customer.googleReviewStatus = 'not_requested';
    return customer;
  }

  private sampleInvoice(): InvoiceInterface {
    const invoice = InvoiceFactory.validate(InvoiceFactory.newInvoice('standard', this.sampleCustomer()));
    invoice.number = 2401;
    invoice.lineItems = this.sampleLineItems();
    invoice.customer = this.sampleCustomer();
    invoice.total = 185000;
    invoice.amountDue = 185000;
    invoice.status = InvoiceStatus.Unpaid;
    invoice.pdf = { file: 'invoice-standard' };
    return this.invoicesService.validate(invoice);
  }

  private samplePaidInvoice(): InvoiceInterface {
    const invoice = this.sampleInvoice();
    invoice.number = 2402;
    invoice.paymentsMade = [{
      amount: invoice.total,
      method: 'Card',
      note: 'Paid in full',
      date: new Date(),
      paymentId: 'dialog-lab-payment',
      transactionSha1: null
    }];
    return this.invoicesService.validate(invoice);
  }

  private sampleLineItems(): LineItem[] {
    return [
      { item: 'Labour', desc: 'Onsite support and setup', qty: 2, price: 7500 },
      { item: 'SSD', desc: '1TB NVMe replacement drive', qty: 1, price: 35000 }
    ];
  }

  private sampleTask(): Task {
    return {
      id: 0,
      date: new Date(),
      type: 'repair',
      status: TaskStatus.InProgress,
      customer: this.sampleCustomer(),
      subject: 'Windowed dialog cleanup',
      description: 'Review a dialog shell and taskbar-friendly frame layout.',
      dueDate: new Date().toISOString(),
      quote: 185000,
      log: [
        {
          date: new Date(),
          text: 'Initial dialog test task created from the lab.',
          type: 'comment'
        }
      ]
    };
  }

  private sampleFeature(): InventoryItemFeature {
    return {
      label: 'Specifications',
      text: 'CPU / RAM / Storage'
    };
  }

  private samplePartsOrder(): PartsOrder {
    return {
      id: 1,
      taskId: 101,
      title: 'Replacement power supply',
      status: PartsOrderStatus.ORDERED,
      supplier: 'Mwave',
      orderNumber: 'PO-TEST-2401',
      quantity: 1,
      unitCostCents: 12900,
      notes: 'Dialog launcher sample order'
    };
  }

  private sampleCalendarEntry(): CalendarEntry {
    return {
      id: 'dialog-lab-entry',
      title: 'Dialog review session',
      subtitle: 'Dialog shell pass',
      description: 'Check spacing, title bars, and actions alignment.',
      scheduledAt: new Date(),
      status: CalendarEntryStatus.Upcoming
    };
  }

  private sampleDateRange(): DateRange {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return { start, end };
  }

  private samplePaymentDetail(): any {
    return {
      invoiceNumber: 2402,
      invoiceType: 'standard',
      invoiceStatus: 'paid',
      paymentId: 'dialog-lab-payment',
      customer: this.sampleCustomer(),
      payment: {
        amount: 185000,
        method: 'Card',
        note: 'Paid in full',
        date: new Date(),
        paymentId: 'dialog-lab-payment',
        transactionSha1: null
      },
      total: 185000,
      amountDue: 0,
      dateCreated: new Date(),
      transactionSha1: null
    };
  }
}
